import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MdOutlineCheck } from 'react-icons/md';
import { AdminFormRow } from '@ui';
import { CreateOpoContactFormValues } from '@lp/features/admin/forms';
import { ControlledTextInput } from '@lp/features/admin/forms/components/inputs/ControlledTextInput';
import { Button, Checkbox, Chip } from '@ds';
import { ControlledYesNoRadio } from '@lp/features/admin/forms/components/inputs/ControlledYesNoRadio';
import { useResendUserInviteMutation } from '@/queries/user';

type OpoContactInviteSectionProps = {
  isReadOnly?: boolean;
  onInviteAsUserChange?: (value: boolean) => void;
};

export const OpoContactInviteSection = React.memo(
  ({ isReadOnly = false, onInviteAsUserChange }: OpoContactInviteSectionProps) => {
    const { control, getValues } = useFormContext<CreateOpoContactFormValues>();
    const { mutateAsync, isPending, isSuccess } = useResendUserInviteMutation();

    const inviteAsUser = useWatch({ control, name: 'inviteAsUser' });
    const isOpoUser = useWatch({ control, name: 'isOpoUser' });
    const inviteSent = useWatch({ control, name: 'inviteSent' });
    const showEmailField = inviteAsUser || isOpoUser;

    const resendInvite = () => {
      const userId = getValues('userId');
      if (userId) mutateAsync(userId);
    };

    return (
      <>
        {isOpoUser ? (
          <AdminFormRow title="Invited as OPO User">
            <Checkbox isSelected isDisabled isReadOnly>
              Yes
            </Checkbox>
          </AdminFormRow>
        ) : (
          <Controller
            name="inviteAsUser"
            control={control}
            render={({ field, fieldState: { invalid } }) => (
              <Checkbox
                checked={field.value}
                onValueChange={onInviteAsUserChange ?? field.onChange}
                isInvalid={invalid}
                className="p-0"
              >
                Invite OPO Contact as OPO User
              </Checkbox>
            )}
          />
        )}

        {showEmailField && (
          <AdminFormRow title="Email" isRequired>
            <ControlledTextInput
              name="email"
              aria-label="Contact Email"
              type="email"
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
            />
          </AdminFormRow>
        )}

        {isOpoUser && (
          <AdminFormRow title="Active Account" isRequired={!inviteSent}>
            {inviteSent ? (
              <div className="flex items-center gap-2">
                <Chip>Invite Sent</Chip>
                <Button
                  variant="outline"
                  size="md"
                  onPress={resendInvite}
                  isLoading={isPending}
                  isDisabled={isSuccess}
                  startContent={isSuccess && <MdOutlineCheck className="text-success-30" />}
                >
                  Resend Invite
                </Button>
              </div>
            ) : (
              <ControlledYesNoRadio name="isActive" isReadOnly={isReadOnly} />
            )}
          </AdminFormRow>
        )}
      </>
    );
  },
);

OpoContactInviteSection.displayName = 'OpoContactInviteSection';
